import TracesWarning from './TracesWarning'
import treeIcon from '@graniot/app_frontend/src/assets/tree.png'
import { hasTracesWarning } from '../../utils/traces'
import React from 'react'
import IconButton from '@material-ui/core/IconButton'
import InputBase from '@material-ui/core/InputBase'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import MapIcon from '@material-ui/icons/Map'
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next'
import RoomIcon from '@material-ui/icons/Room'
import SearchIcon from '@material-ui/icons/Search'
import WarningIcon from '@material-ui/icons/Warning';
import GroupIcon from '@material-ui/icons/Group';
import { useDebounce } from 'usehooks-ts';

import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import CircularProgress from '@material-ui/core/CircularProgress'
import CenterFocusWeakIcon from '@material-ui/icons/CenterFocusWeak'
import CloseIcon from '@material-ui/icons/Close'

import { useAutocompleteGeocoder } from '../../adapters/geocoder'
import { useLocation } from '../../context/LocationContext'
import { isValidCoordinate, getCoordinatesFromString } from '../../utils/map'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}))

// Memoized style objects to prevent recreation on each render
const optionBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& > img': {
    mr: 2,
    ml: 2,
    flexShrink: 0,
  },
  width: '100%',
};

const contentStyles = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flexGrow: 1,
  marginRight: 16
};

const textStyles = {
  minWidth: 0,
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const iconsContainerStyles = {
  display: 'flex',
  flexShrink: 0
};

const iconStyles = {
  flexShrink: 0,
  marginRight: 8
};

const warningIconStyles = {
  color: 'orange'
};

// Consistent right-side spacing for warning icons
const rightIconStyles = {
  flexShrink: 0,
  marginLeft: 8,
};

// Helper function to get icon size based on width
const getIconSize = (width) => (!isWidthUp('sm', width) ? 'small' : 'default');

// Helper to size image icons similarly to MUI icon sizes
const getIconPx = (width) => (!isWidthUp('sm', width) ? 20 : 24);

// Memoized component for warning icons
const WarningIcons = React.memo(({ computed, width, t }) => (
  <div style={iconsContainerStyles}>
    <TracesWarning computed={computed} t={t} fontSize={getIconPx(width)} style={rightIconStyles} />
    {computed.has_overlap && (
      <Tooltip title={t("This parcel overlaps with other parcels")}>
        <WarningIcon
          fontSize={getIconSize(width)}
          style={{ ...rightIconStyles, ...warningIconStyles }}
        />
      </Tooltip>
    )}
    {computed.overlaps_protected_areas && (
      <Tooltip title={t("This parcel overlaps with protected area")}>
        <img
          src={treeIcon}
          alt="Protected area"
          style={{ ...rightIconStyles, width: getIconPx(width), height: getIconPx(width) }}
        />
      </Tooltip>
    )}
    {computed.overlaps_indigenous_areas && (
      <Tooltip title={t("This parcel overlaps with indigenous area")}>
        <GroupIcon
          fontSize={getIconSize(width)}
          style={{ ...rightIconStyles, ...warningIconStyles }}
        />
      </Tooltip>
    )}
  </div>
));

// Memoized component for parcel content
const ParcelOptionContent = React.memo(({ option, width, t }) => {
  const computed = option.properties.computed_attributes || {};
  
  return (
    <>
      <div style={contentStyles}>
        <MapIcon
          fontSize={getIconSize(width)}
          style={iconStyles}
        />
        <Typography
          noWrap
          style={textStyles}
        >
          {option.properties.name}
        </Typography>
      </div>
      <WarningIcons computed={computed} width={width} t={t} />
    </>
  );
});

// Memoized component for location content
const LocationOptionContent = React.memo(({ option, width }) => (
  <div style={contentStyles}>
    <RoomIcon
      fontSize={getIconSize(width)}
    />
    {option.place_name}
  </div>
));

// Main memoized option component
const ParcelOption = React.memo(({ option, width, t }) => {
  const isParcel = option?.properties?.name;
  
  return (
    <Box
      component="li"
      sx={optionBoxStyles}
    >
      {isParcel ? (
        <ParcelOptionContent option={option} width={width} t={t} />
      ) : (
        <LocationOptionContent option={option} width={width} />
      )}
    </Box>
  );
});

function ParcelSelector({
  parcels,
  focusParcel,
  setFocusParcel,
  centerMap,
  width,
}) {
  const classes = useStyles()
  const { t } = useTranslation()

  const ordinatedParcels = React.useMemo(() => {
    if (!parcels) return [];

    // Pre-compute priority and lowercase name for each parcel to avoid repeated calculations
    const parcelsWithMetadata = parcels.map(parcel => {
      const computed = parcel.properties.computed_attributes || {};
      const hasOverlap = computed.has_overlap;
      const overlapsProtected = computed.overlaps_protected_areas;
      const overlapsIndigenous = computed.overlaps_indigenous_areas;
      
      // Calculate priority once
      const priority = hasTracesWarning(computed) ? 4 : (hasOverlap && (overlapsProtected || overlapsIndigenous)) ? 3 :
        ((overlapsProtected || overlapsIndigenous)) ? 2 :
        (hasOverlap) ? 1 : 0;

      return {
        ...parcel,
        _sortPriority: priority,
        _sortName: parcel.properties.name.toLowerCase() // Pre-lowercase for faster comparison
      };
    });

    return parcelsWithMetadata.sort((a, b) => {
      // Use precomputed values for faster comparison
      if (a._sortPriority !== b._sortPriority) {
        return b._sortPriority - a._sortPriority;
      }
      return a._sortName.localeCompare(b._sortName);
    });
  }, [parcels]);


  const [options, setOptions] = React.useState(ordinatedParcels)
  const [inputValue, setInputValue] = React.useState('')
  const debouncedInputValue = useDebounce(inputValue, 300) // 300ms debounce
  const { data, refetch, isLoading } = useAutocompleteGeocoder(debouncedInputValue)
  const { location, setLocation } = useLocation()

  // Reset input and selected parcel when parcels change
  React.useEffect(() => {
    setInputValue('')
    setFocusParcel(null)
  }, [ordinatedParcels, setFocusParcel])

  // Single memoized options computation
  const allOptions = React.useMemo(() => {
    const baseOptions = ordinatedParcels;
    
    if (!debouncedInputValue) {
      return baseOptions;
    }
    
    if (isValidCoordinate(debouncedInputValue)) {
      return [{
        place_name: debouncedInputValue,
        latLng: getCoordinatesFromString(debouncedInputValue)
      }];
    }
    
    // For non-coordinate input, combine parcels with geocoder results
    return data ? [...baseOptions, ...data] : baseOptions;
  }, [ordinatedParcels, debouncedInputValue, data]);

  // Trigger geocoder refetch when debounced input changes (non-coordinate)
  React.useEffect(() => {
    if (debouncedInputValue && !isValidCoordinate(debouncedInputValue)) {
      refetch();
    }
  }, [debouncedInputValue, refetch]);

  // Update options when computed options change
  React.useEffect(() => {
    setOptions(allOptions);
  }, [allOptions]);

  // Memoized event handlers
  const handleSelectionChange = React.useCallback((event, newValue) => {
    if (newValue?.properties?.name) {
      setFocusParcel(newValue);
    } else {
      setLocation(newValue);
      setFocusParcel(null);
    }
  }, [setFocusParcel, setLocation]);

  const handleInputChange = React.useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
  }, []);

  const handleCenterMap = React.useCallback(() => {
    if (focusParcel) {
      centerMap(focusParcel.bbox);
    }
  }, [centerMap, focusParcel]);

  const handleDeselectParcel = React.useCallback(() => {
    setFocusParcel(null);
    setInputValue('');
  }, [setFocusParcel]);

  return (
    <Autocomplete
      id="parcels-and-places-selector"
      loading={isLoading}
      onChange={handleSelectionChange}
      autoHighlight
      inputValue={inputValue}
      onInputChange={handleInputChange}
      style={{
        width: !isWidthUp('sm', width) ? '100%' : 290,
        marginBottom: 10,
      }}
      getOptionLabel={(option) =>
        option?.properties?.name || option?.place_name || ''
      }
      options={options}
      renderOption={(option) => (
        <ParcelOption option={option} width={width} t={t} />
      )}
      renderInput={(params) => (
        <Paper className={classes.root} ref={params.InputProps.ref}>
          <InputBase
            style={{
              backgroundColor: 'transparent',
              width: '100%',
            }}
            type="text"
            multiline={false}
            placeholder={t('select_parcel_or_search_location')}
            {...params.inputProps}
            InputProps={{
              className: classes.input,
            }}
            startAdornment={
              <IconButton>
                {parcels !== undefined ? (
                  <SearchIcon
                    fontSize={
                      !isWidthUp('sm', width)
                        ? 'small'
                        : 'default'
                    }
                  />
                ) : (
                  <CircularProgress size={20} />
                )}
              </IconButton>
            }
            endAdornment={
              <>
                {focusParcel && (
                  <>
                    <IconButton
                      onClick={handleCenterMap}
                      style={{ marginRight: -8 }}
                    >
                      <CenterFocusWeakIcon
                        fontSize={
                          !isWidthUp('sm', width)
                            ? 'small'
                            : 'default'
                        }
                      />
                    </IconButton>
                    <IconButton
                      onClick={handleDeselectParcel}
                      style={{ marginLeft: -8 }}
                    >
                      <CloseIcon
                        fontSize={
                          !isWidthUp('sm', width)
                            ? 'small'
                            : 'default'
                        }
                      />
                    </IconButton>
                  </>
                )}
              </>
            }
          />
        </Paper>
      )}
    />
  )
}

export default withWidth()(ParcelSelector)
